import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useOrder } from '../../hooks/use-order';
import { usePatientManagement } from './hooks/usePatientManagement';
import { PatientSelectionModalProps, CreatePatientFormData } from './types/patient-management.types';
import ModalContent from '../modals/content';
import PatientListView from './components/PatientListView';
import PatientCreateForm from './components/PatientCreateForm';
import StyledError from '../common/form/styled-error';
import PatientProps from '../../entities/patient';

function PatientManagementModal({
  isVisible,
  onClose,
  onPatientAssigned,
  orderId,
  customerId,
}: PatientSelectionModalProps) {
  const { order, refreshOrder } = useOrder();
  const { state, actions } = usePatientManagement(customerId);

  const { patients, totalPages, currentPage, selectedPatient, isLoading, isCreating, searchFilters, viewMode, error } =
    state;

  const {
    searchPatients,
    selectPatient,
    clearSelection,
    setViewMode,
    createPatient,
    assignPatientToOrder,
    resetModal,
  } = actions;

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        searchPatients({ textSearch: searchValue, page: 1 });
      }, 300);
    },
    [searchPatients],
  );

  const handlePatientSelect = useCallback(
    (patient: PatientProps) => {
      selectPatient(patient);
    },
    [selectPatient],
  );

  const handlePatientCreated = useCallback(
    async (formData: CreatePatientFormData): Promise<PatientProps> => {
      const dataWithCustomerId = {
        ...formData,
        customerId,
      };
      const newPatient = await createPatient(dataWithCustomerId);

      selectPatient(newPatient);

      return newPatient;
    },
    [createPatient, selectPatient, customerId],
  );

  const [isAssigning, setIsAssigning] = useState(false);

  const handleClose = useCallback(() => {
    if (isLoading || isCreating || isAssigning) {
      return;
    }

    resetModal();
    clearSelection();
    setViewMode('list');
    onClose();
  }, [resetModal, clearSelection, setViewMode, onClose, isLoading, isCreating, isAssigning]);

  const handleAssignPatient = useCallback(async () => {
    if (!selectedPatient || isAssigning) {
      return;
    }

    setIsAssigning(true);

    try {
      await assignPatientToOrder(selectedPatient.id, orderId);

      await refreshOrder();

      onPatientAssigned(selectedPatient);
      handleClose();
    } finally {
      setIsAssigning(false);
    }
  }, [selectedPatient, isAssigning, orderId, assignPatientToOrder, refreshOrder, onPatientAssigned, handleClose]);

  const handleModalClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      setViewMode('list');
      clearSelection();
      searchPatients({ page: 1, textSearch: '' });
    }
  }, [isVisible, setViewMode, clearSelection, searchPatients]);

  const modalTitle = viewMode === 'list' ? 'Select Patient' : 'Create New Patient';

  const getAssignButtonText = () => {
    if (isAssigning) {
      return 'Assigning...';
    }
    if (selectedPatient?.id === order?.order?.patientId) {
      return 'Keep Current Patient';
    }

    return 'Assign Patient';
  };

  return (
    <ModalContent title={modalTitle} visible={isVisible} onCloseClick={handleModalClose} fullHeight>
      <div className="h-full flex flex-col min-h-0">
        {error && (
          <div className="mb-4 flex-shrink-0">
            <StyledError title={error} />
          </div>
        )}

        {customerId && (
          <div className="mb-4 flex-shrink-0 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">Showing patients for customer ID: {customerId}</p>
          </div>
        )}

        {viewMode === 'list' ? (
          <div className="flex-1 flex flex-col min-h-0">
            <PatientListView
              patients={patients}
              selectedPatient={selectedPatient}
              onPatientSelect={handlePatientSelect}
              isLoading={isLoading}
              searchValue={searchFilters.textSearch}
              onSearchChange={handleSearchChange}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => searchPatients({ page })}
              onCreateNew={() => setViewMode('create')}
              currentPatientId={order?.order?.patientId}
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-4 flex-shrink-0">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignPatient}
                disabled={!selectedPatient || isLoading || isAssigning}
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedPatient?.id === order?.order?.patientId
                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                }`}
              >
                {getAssignButtonText()}
              </button>
            </div>
          </div>
        ) : (
          <PatientCreateForm
            onCancel={() => setViewMode('list')}
            onPatientCreated={handlePatientCreated}
            isLoading={isCreating}
          />
        )}
      </div>
    </ModalContent>
  );
}

export default PatientManagementModal;
